import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { categories } from '../../../mocks/products';

export default function AnimationsShowcase() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setIsVisible(true);
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % categories.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="py-20 bg-white relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 -left-24 w-96 h-96 bg-gradient-to-br from-orange-200 to-pink-200 rounded-full blur-3xl opacity-30 animate-float"></div>
        <div className="absolute top-1/2 -right-24 w-96 h-96 bg-gradient-to-br from-purple-200 to-orange-200 rounded-full blur-3xl opacity-30 animate-float-delayed"></div>
        <div className="absolute -bottom-24 left-1/3 w-96 h-96 bg-gradient-to-br from-pink-200 to-orange-200 rounded-full blur-3xl opacity-30 animate-float"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Shop by <span className="bg-gradient-to-r from-orange-500 to-pink-600 bg-clip-text text-transparent">Category</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Explore our diverse collection of premium products across multiple categories
          </p>
        </div>

        {/* Categories Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {categories.map((category, index) => (
            <div
              key={category.id}
              className={`group relative bg-gradient-to-br from-white to-orange-50 rounded-2xl p-8 border-2 transition-all duration-500 cursor-pointer ${
                activeIndex === index 
                  ? 'border-orange-500 shadow-2xl shadow-orange-500/30 scale-105' 
                  : 'border-gray-200 hover:border-pink-300 hover:shadow-xl'
              }`}
              onClick={() => { setActiveIndex(index); navigate(`/shop?category=${category.key}`); }}
              style={{
                animation: isVisible ? `fadeInUp 0.6s ease-out ${index * 0.1}s both` : 'none'
              }}
            >
              <div className={`w-16 h-16 flex items-center justify-center rounded-2xl mb-6 transition-all duration-500 ${
                activeIndex === index
                  ? 'bg-gradient-to-br from-orange-500 to-pink-600 text-white scale-110'
                  : 'bg-orange-100 text-orange-600 group-hover:bg-gradient-to-br group-hover:from-orange-500 group-hover:to-pink-600 group-hover:text-white'
              }`}>
                <i className={`${category.icon} text-3xl`}></i>
              </div>
              
              <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-orange-600 transition-colors duration-300">
                {category.name}
              </h3>
              <p className="text-gray-600 text-sm mb-4">{category.count}+ Products</p>
              
              <div
                className="flex items-center text-orange-600 font-semibold text-sm group-hover:translate-x-2 transition-transform duration-300"
                onClick={(e) => { e.stopPropagation(); navigate(`/shop?category=${category.key}`); }}
              >
                <span>Browse Now</span>
                <i className="ri-arrow-right-line ml-2"></i>
              </div>

              {/* Animated Border */}
              <div className={`absolute inset-0 rounded-2xl transition-opacity duration-500 ${
                activeIndex === index ? 'opacity-100' : 'opacity-0'
              }`}>
                <div className="absolute inset-0 rounded-2xl border-2 border-orange-500 animate-pulse"></div>
              </div>
            </div>
          ))}
        </div>

        {/* Feature Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            {
              icon: 'ri-truck-line',
              title: 'Fast Delivery',
              description: 'Across Dubai & all UAE Emirates',
              color: 'from-orange-500 to-pink-600'
            },
            {
              icon: 'ri-shield-check-line',
              title: 'Secure Payment',
              description: '100% protected transactions',
              color: 'from-pink-500 to-purple-600'
            },
            {
              icon: 'ri-customer-service-2-line',
              title: '24/7 Support',
              description: 'Always here to help',
              color: 'from-purple-500 to-orange-500'
            }
          ].map((feature, index) => (
            <div
              key={index}
              className="group bg-white rounded-2xl p-8 border-2 border-gray-200 transition-all duration-500 hover:border-orange-300 hover:shadow-xl hover:-translate-y-2 cursor-pointer"
              style={{
                animation: isVisible ? `fadeInUp 0.6s ease-out ${(index + 4) * 0.1}s both` : 'none'
              }}
            >
              <div className={`w-16 h-16 flex items-center justify-center rounded-2xl bg-gradient-to-br ${feature.color} text-white mb-6 transition-transform duration-500 group-hover:scale-110 group-hover:rotate-12`}>
                <i className={`${feature.icon} text-3xl`}></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-orange-600 transition-colors duration-300">
                {feature.title}
              </h3>
              <p className="text-gray-600">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}